import React from 'react'
import { ImageOff, FileText, Files } from 'lucide-react'
import { cn } from 'lib/utils'
import type { NotePlan } from './imageUploadWizard.types'

interface NotePlanStepProps {
	imageCount: number
	value: NotePlan | null
	onChange: (plan: NotePlan) => void
}

const OPTIONS: {
	plan: NotePlan
	icon: typeof FileText
	title: string
	describe: (count: number) => string
}[] = [
	{
		plan: 'none',
		icon: ImageOff,
		title: 'No note',
		describe: count =>
			count === 1 ? 'Just add the image to the gallery.' : 'Just add the images to the gallery.',
	},
	{
		plan: 'single',
		icon: FileText,
		title: 'One note for all images',
		describe: count => 'Write one note that holds all ' + count + ' images.',
	},
	{
		plan: 'per-image',
		icon: Files,
		title: 'A separate note per image',
		describe: count => 'Write ' + count + ' notes, one for each image.',
	},
]

/**
 * The note question.
 *
 * Built as buttons in a radiogroup rather than a ShadCN RadioGroup because
 * @radix-ui/react-radio-group is not a dependency and three choices do not
 * justify adding one.
 */
const NotePlanStep: React.FC<NotePlanStepProps> = ({ imageCount, value, onChange }) => {
	// One image makes "one note for all" and "one note per image" the same
	// thing, so the split is only offered when it means something.
	const options = imageCount > 1 ? OPTIONS : OPTIONS.filter(o => o.plan !== 'per-image')

	return (
		<div>
			<p className="mb-3 text-sm text-muted-foreground">
				{imageCount === 1
					? 'Do you want to add a note with this image?'
					: 'Do you want to add a note with these ' + imageCount + ' images?'}
			</p>

			<div role="radiogroup" aria-label="Note options" className="space-y-2">
				{options.map(option => {
					const Icon = option.icon
					const selected = value === option.plan

					return (
						<button
							key={option.plan}
							type="button"
							role="radio"
							aria-checked={selected}
							onClick={() => onChange(option.plan)}
							className={cn(
								'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors',
								selected
									? 'border-primary bg-primary/5'
									: 'border-border bg-white hover:border-primary/50',
							)}
						>
							<span
								className={cn(
									'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
									selected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
								)}
							>
								<Icon className="h-4 w-4" />
							</span>
							<span className="min-w-0">
								<span className="block text-sm font-semibold text-foreground">
									{option.title}
								</span>
								<span className="block text-xs text-muted-foreground">
									{option.describe(imageCount)}
								</span>
							</span>
						</button>
					)
				})}
			</div>
		</div>
	)
}

export default NotePlanStep
